<template>
	<div
		v-measure-width="{
			callback: onInteractionOverlayWidthChange,
			sizes: VIDEO_INTERACTION_OVERLAY_CONFIG
		}"
		class="play-toggle-overlay"
		:class="[
			`-${interactionOverlaySize}`,
		]"
	>
		<div class="play-toggle-overlay__controls">
			<div
				ref="playIndicator"
				class="play-toggle-overlay__indicator"
				:class="{
					'is-visible': forcePlayVisible || showPlay,
					'is-transitioning': transitioningPlay,
				}"
				@click="togglePlayPause"
			>
				<Icon :icon-name="'button-play-filled'" />
			</div>
			<div
				ref="pauseIndicator"
				class="play-toggle-overlay__indicator"
				:class="{
					'is-visible': showPause,
					'is-transitioning': transitioningPause,
				}"
				@click="togglePlayPause"
			>
				<Icon :icon-name="'button-pause-filled'" />
			</div>
		</div>
	</div>
</template>

<script>
import { VIDEO_INTERACTION_OVERLAY_CONFIG } from '@shared/constants/interactionOverlaySizes';
import VMeasureWidth from '@shared/directives/v-measure-width';

export default {
	name:       'PlayToggleOverlay',
	directives: {
		measureWidth: VMeasureWidth,
	},
	props: {
		forcePlayVisible: {
			type:    Boolean,
			default: false,
		},
		isPlaying: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			showPlay:               !this.isPlaying,
			showPause:              this.isPlaying,
			timeouts:               [],
			transitioningPlay:      false,
			transitioningPause:     false,
			interactionOverlaySize: 'lg',
			VIDEO_INTERACTION_OVERLAY_CONFIG,
		};
	},
	watch: {
		isPlaying(newVal) {
			this.showPlay = !newVal;
			this.showPause = newVal;
		},
	},
	mounted() {
		this.$refs.playIndicator.addEventListener('transitionend', this.handlePlayTransitionEnd);
		this.$refs.pauseIndicator.addEventListener('transitionend', this.handlePauseTransitionEnd);
	},
	beforeDestroy() {
		this.$refs.playIndicator.removeEventListener('transitionend', this.handlePlayTransitionEnd);
		this.$refs.pauseIndicator.removeEventListener('transitionend', this.handlePauseTransitionEnd);
	},
	methods: {
		onInteractionOverlayWidthChange(newSize) {
			this.interactionOverlaySize = newSize;
		},
		reset(callback = null, resetPlayPause = true) {
			for (const timeout of this.timeouts) {
				clearTimeout(timeout);
			}
			this.timeouts = [];
			if (resetPlayPause) {
				this.showPlay = false;
				this.showPause = false;
			}
			if (callback) this.$nextTick(callback);
		},
		handlePlay() {
			this.transitioningPlay = true;
			this.reset(() => {
				this.showPause = true;
			});
		},
		handlePause() {
			this.transitioningPause = true;
			this.reset(() => {
				this.showPlay = true;
			});
		},
		handlePlayTransitionEnd(e) {
			if (getComputedStyle(e.target).opacity === '0') {
				this.transitioningPlay = false;
			}
		},
		handlePauseTransitionEnd(e) {
			if (getComputedStyle(e.target).opacity === '0') {
				this.transitioningPause = false;
			}
		},
		togglePlayPause(e) {
			e.stopPropagation();
			this.$emit('toggle-play-pause');
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

$indicator-background: rgba(6, 7, 13, 0.4);
$animation-easing: cubic-bezier(0.25, 0, 0.66, 0.99);

.play-toggle-overlay {
	// &.-sm {}
	--play-pause-size: 72px;
	--play-pause-icon-size: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;

	&.-md {
		--play-pause-size: 96px;
		--play-pause-icon-size: 36px;
	}

	&.-lg {
		--play-pause-size: 114px;
		--play-pause-icon-size: 38px;
	}
}

.play-toggle-overlay__controls {
	position: relative;
	width: var(--play-pause-size);
	opacity: 1;
	transition: opacity $player-overlay-animation;
}

.play-toggle-overlay__indicator {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.8em;
	border-radius: 100%;
	white-space: pre;
	line-height: 1;
	color: var(--color-soft-white);
	background-color: $indicator-background; // stylelint-disable-line declaration-property-value-allowed-list
	opacity: 0;
	transition: opacity 0.3s $animation-easing;
	position: absolute;
	top: 50%;
	left: 50%;
	justify-content: center;
	width: var(--play-pause-size);
	height: var(--play-pause-size);
	transform: translate(-50%, -50%);

	> div { // stylelint-disable-line max-nesting-depth, selector-max-type
		width: var(--play-pause-icon-size);
		height: var(--play-pause-icon-size);
	}

	&.is-visible {
		opacity: 1;
	}

	svg { // stylelint-disable-line selector-max-type
		fill: var(--color-white);
	}
}
</style>
